import React, { useState, useRef } from 'react';
import { Box } from '@mui/material';
import ChipComponent from './ChipComponent/ChipComponent';
import StatusSelector from './StatusSelector';
import { TestLaunchStatus } from '../../enums/TestLaunchEnums';
import { getTestLaunchStatusVariant, getStatusIcon } from './ChipComponent/chipVariants';

/**
 * ChangeStatus - A generic chip component with interactive status selection
 * 
 * Props:
 * - status: string (current status value)
 * - availableStatuses: array (list of available status options)
 * - getVariant: function (maps status to color variant)
 * - getIcon: function (maps status to icon)
 * - formatStatus: function (formats status text for display)
 * - onStatusChange: function (callback when status changes)
 * - disabled: boolean (disable edit functionality)
 * - confirmChange: boolean (show confirmation modal, default: true)
 * - confirmTitle: string (confirmation modal title)
 * - confirmMessage: string (confirmation modal message)
 * - size: string (chip size: 'small' or 'medium')
 * - showTooltip: boolean (show tooltip on hover)
 */
const ChangeStatus = ({
  status,
  availableStatuses = [],
  getVariant,
  getIcon,
  formatStatus = (status) => status,
  onStatusChange,
  disabled = false,
  confirmChange = true,
  confirmTitle = "Confirm Status Change",
  confirmMessage = "Are you sure you want to change the status?",
  size = 'medium',
  showTooltip = true,
}) => {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const chipRef = useRef(null);

  // Business rule: Test Launch statuses are only editable if not COMPLETED or ABORTED
  const isEditable = !disabled && ![TestLaunchStatus.COMPLETED, TestLaunchStatus.ABORTED].includes(status);

  const handleEdit = () => {
    if (isEditable) {
      setMenuAnchor(chipRef.current);
    }
  };

  const handleClose = () => {
    setMenuAnchor(null);
  };

  const handleStatusChange = (newStatus) => {
    if (newStatus !== status && onStatusChange) {
      onStatusChange(newStatus);
    }
    handleClose();
  };

  return (
    <>
      <Box ref={chipRef}>
        <ChipComponent
          variant={getVariant ? getVariant(status) : 'default'}
          icon={getIcon ? getIcon(status) : null}
          size={size}
          editable={isEditable}
          onEdit={handleEdit}
          showTooltip={showTooltip}
        >
          {formatStatus(status)}
        </ChipComponent>
      </Box>

      <StatusSelector
        currentStatus={status}
        availableStatuses={availableStatuses}
        getVariant={getVariant}
        getIcon={getIcon}
        formatStatus={formatStatus}
        onChange={handleStatusChange}
        confirmChange={confirmChange}
        confirmTitle={confirmTitle}
        confirmMessage={confirmMessage}
        anchorEl={menuAnchor}
        onClose={handleClose}
      />
    </>
  );
};

export default ChangeStatus;

